// «Машины»: где работают агенты. По каждой машине (раннеру) — чья учётка и какой движок, что выполняется сейчас,
// загрузка за сутки или неделю (запуски, задачи, токены, стоимость в ценах API, доля занятых мест), упоры в лимит
// подписки и последние задачи. Сверху — доля работы каждой машины за выбранный период.
import { useEffect, useId, useState } from 'react'
import { api } from '../api.ts'
import { AgentLabel } from '../components/AgentAvatar.tsx'
import { FleetControls } from '../components/FleetControls.tsx'
import { Badge, Button, Empty, ErrorBox, Field, Loading, PageHeader, Section, Stat, cx } from '../components/ui.tsx'
import { useAsync, useBusy, useDebounced } from '../lib/hooks.ts'
import { formatDateTime, formatUsd, timeAgo } from '../lib/format.ts'
import { TaskLink } from '../router.tsx'
import { refreshSettings, useLive, useNow, useReconnect, useStream } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import { TASK_STATUS_LABELS, type Machine, type RunnerUsage, type RunnerUsageWindow, type TaskStatus, type Tone } from '../types.ts'

const ROLE: Record<RunnerUsage['role'], { label: string; tone: Tone; hint: string }> = {
  main: { label: 'основная', tone: 'ok', hint: 'получает работу с учётом выбранного основного движка' },
  reserve: { label: 'резерв', tone: 'neutral', hint: 'берёт новую работу, когда у основных нет мест или они на паузе' },
  pilot: { label: 'пилот', tone: 'accent', hint: 'только задачи владельца «только чтение»' },
  read_only: { label: 'только чтение', tone: 'neutral', hint: 'только ходы без записи' },
}
const ENGINE: Record<RunnerUsage['engine'], string> = { claude: 'Claude', codex: 'ChatGPT · Codex' }
// цвета машин в общей полосе — по порядку карточек
const SWATCH = ['bg-accent', 'bg-ok', 'bg-warn', 'bg-danger', 'bg-subtle']

const pct = (x: number) => `${Math.round(x * 100)}%`
const tokens = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)} млн` : n >= 1e3 ? `${Math.round(n / 1e3)} тыс.` : String(n))

/** Доля работы машин за период: одна полоса, у каждой машины — своя часть по числу запусков. */
function Share({ runners, pick }: { runners: RunnerUsage[]; pick: (r: RunnerUsage) => RunnerUsageWindow }) {
  const total = runners.reduce((a, r) => a + pick(r).runs, 0)
  if (!total) return <p className="text-[13px] text-muted">За этот период запусков не было.</p>
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-2" role="img" aria-label="Доля запусков по машинам">
        {runners.map((r, i) => {
          const w = pick(r).runs / total
          return w > 0 ? <div key={r.id} className={SWATCH[i % SWATCH.length]} style={{ width: `${w * 100}%` }} title={`${r.name}: ${pick(r).runs} запусков (${pct(w)})`} /> : null
        })}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-fg-2">
        {runners.map((r, i) => (
          <li key={r.id} className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className={cx('inline-block size-2.5 rounded-sm', SWATCH[i % SWATCH.length])} />
            {r.name} — {pct(pick(r).runs / total)} запусков, {formatUsd(pick(r).cost_usd)}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Занятые места по часам за последние сутки: высота — доля мест машины, последний столбец — текущий час. */
function Hours({ r }: { r: RunnerUsage }) {
  const now = new Date()
  return (
    <div>
      <div className="flex h-12 items-end gap-[2px]" role="img" aria-label="Занятые места по часам за сутки">
        {r.hourly.map((v, i) => {
          const share = r.capacity > 0 ? Math.min(1, v / r.capacity) : 0
          const hour = (now.getHours() - (r.hourly.length - 1 - i) + 24) % 24
          return (
            <div key={i} className="flex h-full flex-1 items-end bg-surface-2" title={`${String(hour).padStart(2, '0')}:00 — в среднем занято ${v.toFixed(1)} из ${r.capacity} мест`}>
              <div className="w-full bg-accent" style={{ height: `${Math.max(share > 0 ? 4 : 0, share * 100)}%` }} />
            </div>
          )
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-subtle">
        <span>сутки назад</span>
        <span>сейчас</span>
      </div>
    </div>
  )
}

function RunnerCard({ r, w, swatch, reload }: { r: RunnerUsage; w: RunnerUsageWindow; swatch: string; reload: () => void }) {
  const now = useNow()
  const settings = useLive((s) => s.settings)
  const role = ROLE[r.role]
  const busyNow = r.running_jobs.length
  const profile = settings?.model_profiles[r.engine]
  const desiredState = r.desired_state ?? 'active'
  const effectiveState = r.effective_state ?? 'active'
  return (
    <Section
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          <span aria-hidden="true" className={cx('inline-block size-2.5 rounded-sm', swatch)} />
          {r.name}
          <Badge tone={r.status === 'online' ? 'ok' : 'danger'}>{r.status === 'online' ? 'на связи' : `не на связи ${timeAgo(r.last_seen, now)}`}</Badge>
          {effectiveState !== 'active' ? <Badge tone="warn">{effectiveState === 'draining' && busyNow ? 'завершает ходы' : 'на паузе'}</Badge> : null}
          <Badge tone={role.tone} title={role.hint}>{role.label}</Badge>
          <Badge tone="neutral">{ENGINE[r.engine]}</Badge>
        </span>
      }
      actions={<FleetControls path={`/api/runners/${encodeURIComponent(r.id)}`} state={desiredState} name={r.name} onChanged={reload} />}
    >
      <p className="mb-3 text-[12.5px] text-muted">
        <span className="tnum">Занято {busyNow} из {r.capacity} мест</span>
        {profile ? <> · модели по настройкам: <span className="font-mono">{profile.standard}</span> / <span className="font-mono">{profile.strong}</span></> : null}
      </p>
      {(r.transfer_pending ?? 0) > 0 ? <p className="mb-3 rounded-ctl bg-warn-soft px-3 py-2 text-[13px]">Передача: {r.transfer_pending} запусков. Новые ходы начнутся после подтверждения остановки прежних.</p> : null}
      {r.transfer_blocked_reason ? <p className="mb-3 rounded-ctl bg-warn-soft px-3 py-2 text-[13px]">{r.transfer_blocked_reason}</p> : null}
      {desiredState === 'active' && effectiveState !== 'active' ? <p className="mb-3 text-[12.5px] text-muted">Раннер разрешён, но вся машина на паузе. Возобновите машину выше.</p> : null}
      {r.paused_until ? (
        <p className="mb-3 rounded-ctl border border-line bg-warn-soft px-3 py-2 text-[13px] text-fg">
          Новые запуски временно приостановлены до {formatDateTime(r.paused_until)}; работу могут взять другие подходящие раннеры.
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="запусков" value={w.runs} hint={w.failed ? `не удались: ${w.failed}` : undefined} tone={w.failed > w.runs / 5 ? 'warn' : undefined} />
        <Stat label="задач" value={w.tasks} />
        <Stat label="занятость мест" value={pct(w.utilization)} hint={`${w.busy_hours.toFixed(1)} место-ч`} />
        <Stat label="в ценах API" value={formatUsd(w.cost_usd)} hint={r.engine === 'codex' ? 'оценка, на подписке ChatGPT' : 'на подписке Claude'} />
        <Stat label="токены: вход / выход" value={`${tokens(w.input_tokens)} / ${tokens(w.output_tokens)}`} />
        <Stat
          label="упирались в лимит"
          value={w.quota_hits}
          tone={w.quota_hits ? 'warn' : undefined}
          hint={r.last_quota_at ? `последний раз ${timeAgo(r.last_quota_at, now)}` : 'не было'}
        />
      </div>
      <div className="mt-4">
        <h3 className="mb-1.5 text-[12.5px] font-medium text-muted">Загрузка по часам за сутки</h3>
        <Hours r={r} />
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="min-w-0">
          <h3 className="mb-1.5 text-[12.5px] font-medium text-muted">Выполняется сейчас</h3>
          {r.running_jobs.length ? (
            <ul className="flex flex-col gap-1.5">
              {r.running_jobs.map((j) => (
                <li key={j.job_id} className="flex min-w-0 flex-wrap items-center gap-x-2 text-[13px]">
                  <AgentLabel id={j.agent_id} size="xs" link />
                  {j.task_id ? (
                    <TaskLink id={j.task_id} className="min-w-0 truncate hover:underline">
                      <span className="font-mono text-[11.5px] text-subtle">{j.task_key}</span> {j.task_title}
                    </TaskLink>
                  ) : <span className="text-muted">без задачи</span>}
                  <span className="text-[11.5px] text-subtle">· {timeAgo(j.started_at, now)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-[13px] text-muted">Ничего — места свободны.</p>}
        </div>
        <div className="min-w-0">
          <h3 className="mb-1.5 text-[12.5px] font-medium text-muted">Последние задачи на этой машине</h3>
          {r.recent_tasks.length ? (
            <ul className="flex flex-col gap-1.5">
              {r.recent_tasks.map((t) => (
                <li key={t.task_id} className="flex min-w-0 items-center gap-2 text-[13px]">
                  <TaskLink id={t.task_id} className="min-w-0 flex-1 truncate hover:underline">
                    <span className="font-mono text-[11.5px] text-subtle">{t.task_key}</span> {t.task_title}
                  </TaskLink>
                  <span className="shrink-0 text-[11.5px] text-subtle">
                    {TASK_STATUS_LABELS[t.task_status as TaskStatus] ?? t.task_status} · {timeAgo(t.last_at, now)}
                  </span>
                </li>
              ))}
            </ul>
          ) : <p className="text-[13px] text-muted">Задач ещё не было.</p>}
        </div>
      </div>
    </Section>
  )
}

function MachineGroup({ machine, runners, pick, reload, swatchFor }: {
  machine: Machine; runners: RunnerUsage[]; pick: (r: RunnerUsage) => RunnerUsageWindow; reload: () => void; swatchFor: (id: string) => string
}) {
  const [capacity, setCapacity] = useState(machine.capacity?.toString() ?? '')
  const [busy, run] = useBusy()
  const id = useId()
  useEffect(() => setCapacity(machine.capacity?.toString() ?? ''), [machine.capacity])
  const valid = !capacity || (Number.isInteger(Number(capacity)) && Number(capacity) >= 1 && Number(capacity) <= 200)
  const dirty = capacity !== (machine.capacity?.toString() ?? '')
  const saveCapacity = () => void run(async () => {
    try {
      await api.patch(`/api/machines/${encodeURIComponent(machine.id)}`, { capacity: capacity ? Number(capacity) : null })
      toast('Общий лимит машины сохранён', 'success')
      reload()
    } catch (e) { toastError(e, 'Не удалось сохранить лимит') }
  })
  const fallbackCapacity = machine.reported_capacity ?? runners.reduce((total, r) => total + r.capacity, 0)
  return <Section title={<span className="inline-flex flex-wrap items-center gap-2">{machine.name}
    <Badge tone={machine.status === 'online' ? 'ok' : 'neutral'}>{machine.status === 'online' ? 'на связи' : 'не на связи'}</Badge>
    {machine.state !== 'active' ? <Badge tone="warn">{machine.state === 'draining' && machine.running ? 'завершает ходы' : 'на паузе'}</Badge> : null}
  </span>} actions={<FleetControls path={`/api/machines/${encodeURIComponent(machine.id)}`} state={machine.state} name={machine.name} onChanged={reload} />}>
    <div className="mb-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] text-muted">Занято <span className="tnum font-semibold text-fg">{machine.running} из {machine.capacity ?? fallbackCapacity}</span> мест на всей машине. Claude и Codex используют этот общий лимит.</p>
        <p className="mt-1 text-[12px] text-subtle">Управление машиной действует на все её раннеры. Отдельные паузы раннеров сохраняются после возобновления машины.</p>
      </div>
      <Field label="Общий лимит мест" htmlFor={id} hint={`Автоматически: ${fallbackCapacity}. Пустое поле — автоматически.`}>
        <div className="flex items-center gap-2">
          <input id={id} className="ctl w-28" type="number" min={1} max={200} value={capacity} placeholder="Авто"
            onChange={(e) => setCapacity(e.target.value)} aria-invalid={!valid} />
          {dirty ? <Button size="sm" loading={busy} disabled={busy || !valid} onClick={saveCapacity}>Сохранить лимит</Button> : null}
        </div>
      </Field>
    </div>
    <div className="flex flex-col gap-3">
      {runners.map((r) => <RunnerCard key={r.id} r={r} w={pick(r)} swatch={swatchFor(r.id)} reload={reload} />)}
      {!runners.length ? <p className="text-[13px] text-muted">Нет недавно подключённых раннеров.</p> : null}
    </div>
  </Section>
}

export function MachinesPage() {
  const [period, setPeriod] = useState<'day' | 'week'>('day')
  const data = useAsync(async (signal) => {
    const [runners, machines] = await Promise.all([
      api.get<RunnerUsage[]>('/api/runners/usage', { signal }), api.get<Machine[]>('/api/machines', { signal }),
    ])
    return { runners, machines }
  }, [], { keep: true })
  const reloadSoon = useDebounced(data.reload, 3000)
  useStream((ev) => {
    if (ev.type === 'job' || ev.type === 'runner') reloadSoon()
  })
  useReconnect(data.reload)
  const reload = () => { data.reload(); void refreshSettings() }
  const pick = (r: RunnerUsage) => r[period]
  const toggle = (
    <div className="inline-flex rounded-ctl border border-line p-0.5" role="group" aria-label="Период">
      {(['day', 'week'] as const).map((p) => (
        <button key={p} type="button" onClick={() => setPeriod(p)} aria-pressed={period === p}
          className={cx('rounded-[6px] px-2.5 py-1 text-[12.5px]', period === p ? 'bg-surface-3 font-medium text-fg' : 'text-muted hover:text-fg')}>
          {p === 'day' ? 'Сутки' : 'Неделя'}
        </button>
      ))}
    </div>
  )
  return (
    <div className="page">
      <PageHeader
        crumb="OMA"
        title="Машины"
        lede="Машины, их Claude- и Codex-раннеры, общие лимиты и передача задач между ними."
        actions={toggle}
      />
      <p className="mb-4 text-[13px] text-muted">«Завершить ходы и пауза» даёт текущим ходам доработать. «Пауза и передача» останавливает их и переносит работу с контекстом на доступные раннеры. Новые ходы на паузе не запускаются.</p>
      {data.error ? <ErrorBox error={data.error} onRetry={data.reload} /> : null}
      {!data.data ? (data.error ? null : <Loading />) : !data.data.machines.length ? (
        <Empty icon="server">Ни одна машина не выходила на связь за неделю.</Empty>
      ) : (
        <div className="flex flex-col gap-4">
          <Section title={period === 'day' ? 'Доля работы за сутки' : 'Доля работы за неделю'}>
            <Share runners={data.data.runners} pick={pick} />
          </Section>
          {data.data.machines.map((machine) => <MachineGroup key={machine.id} machine={machine}
            runners={data.data!.runners.filter((r) => r.machine_id === machine.id)} pick={pick} reload={reload}
            swatchFor={(id) => SWATCH[data.data!.runners.findIndex((r) => r.id === id) % SWATCH.length]!} />)}
        </div>
      )}
    </div>
  )
}
